import styled, { type CSSObject, css, type DefaultTheme } from 'styled-components'

import {
  DROPDOWN_LIST_ITEM_FONT_SIZE_MAP,
  DROPDOWN_LIST_ITEM_GAP,
  DROPDOWN_LIST_ITEM_HEIGHT_MAP,
  DROPDOWN_LIST_ITEM_LINE_HEIGHT_MAP,
  DROPDOWN_LIST_ITEM_PADDING_HORIZONTAL,
  DROPDOWN_LIST_SHADOW,
} from './dropdown-list.constants'
import {
  DropdownListItemPalette,
  DropdownListItemSize,
  DropdownListItemStyleOptions,
} from './dropdown-list.types'

// Note: the design system's dropdown container - shared by Select, ChipSelect and DropdownMenu
// so their panels can't drift apart.
export const dropdownListStyles = css(
  ({ theme }) => css`
    background: ${theme.colors.white};
    border: ${theme.border.size['1']} solid ${theme.colors.gray500};
    border-radius: ${theme.border.radius['6']};
    box-shadow: ${DROPDOWN_LIST_SHADOW};
    box-sizing: border-box;
  `,
)

// Note: dropdown-only scrollbar per the design (6px, gray300 track, black100 thumb). Kept
// separate from the shared `thinScrollbar` on purpose - that one styles every other scroll area.
export const dropdownListScrollbar = css(
  ({ theme }) => css`
    &::-webkit-scrollbar {
      width: 0.6rem;
    }

    &::-webkit-scrollbar-track {
      background: ${theme.colors.gray300};
      border-radius: ${theme.border.radius.full};
    }

    &::-webkit-scrollbar-thumb {
      background: ${theme.colors.black100};
      border-radius: ${theme.border.radius.full};
    }

    scrollbar-width: thin;
    scrollbar-color: ${`${theme.colors.black100} ${theme.colors.gray300}`};
  `,
)

const getItemPalette = (
  theme: DefaultTheme,
  { isActive, isDisabled, isDanger, palette }: Omit<DropdownListItemStyleOptions, 'size'>,
): DropdownListItemPalette | null => {
  if (isDisabled) return null
  if (palette) return palette
  if (isDanger) return { background: theme.colors.red100, color: theme.colors.red500 }
  if (isActive) return { background: theme.colors.primary200, color: theme.colors.primary800 }
  return {
    background: theme.colors.white,
    color: theme.colors.primary800,
    hoverBackground: theme.colors.gray300,
  }
}

const itemPaddingVertical: Record<DropdownListItemSize, keyof DefaultTheme['space']> = {
  medium: 8,
  small: 4,
}

// Note: the design system's "Dropdown List Item" row (Default, Hover & Focused, Active, Disabled,
// Dangerous). Returned as an object so each consumer can layer its own extras (tree indent, ...)
// on top without re-declaring the shared metrics; colored consumers pass a `palette` instead.
export const getDropdownListItemStyles = (
  theme: DefaultTheme,
  { size, ...state }: DropdownListItemStyleOptions,
): CSSObject => {
  const palette = getItemPalette(theme, state)

  const getInteractionStyles = (): CSSObject => {
    if (!palette) return {}
    // Note: rows with a hover color use it for keyboard focus too (the design's "Hover &
    // Focused"); the rest (Active, Dangerous) get an inset ring so focus is never invisible.
    if (palette.hoverBackground) {
      return {
        '&:focus-visible': { backgroundColor: palette.hoverBackground },
        '&:hover': { backgroundColor: palette.hoverBackground },
      }
    }
    return {
      '&:focus-visible': { boxShadow: `inset 0 0 0 ${theme.border.size['2']} ${palette.color}` },
    }
  }

  return {
    ...getInteractionStyles(),
    alignItems: 'center',
    backgroundColor: palette ? palette.background : theme.colors.gray100,
    border: 'none',
    boxSizing: 'border-box',
    color: palette ? palette.color : theme.colors.primary400,
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    fontSize: DROPDOWN_LIST_ITEM_FONT_SIZE_MAP[size],
    fontWeight: 400,
    gap: DROPDOWN_LIST_ITEM_GAP,
    lineHeight: DROPDOWN_LIST_ITEM_LINE_HEIGHT_MAP[size],
    minHeight: DROPDOWN_LIST_ITEM_HEIGHT_MAP[size],
    outline: 'none',
    padding: `${theme.space[itemPaddingVertical[size]]} ${DROPDOWN_LIST_ITEM_PADDING_HORIZONTAL}`,
    textAlign: 'left',
    transition: `background-color ${theme.transition.duration.sm} ease`,
    width: '100%',
  }
}

export const DropdownList = styled.div`
  ${dropdownListStyles};

  display: flex;
  flex-direction: column;
  overflow: hidden;
`

export const DropdownListItem = styled.div<{
  $size: DropdownListItemSize
  $isActive?: boolean
  $isDisabled?: boolean
  $isDanger?: boolean
  $palette?: DropdownListItemPalette
}>(({ theme, $size, $isActive, $isDisabled, $isDanger, $palette }) =>
  getDropdownListItemStyles(theme, {
    isActive: $isActive,
    isDanger: $isDanger,
    isDisabled: $isDisabled,
    palette: $palette,
    size: $size,
  }),
)
